import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Body2, Subtitle1, makeStyles, tokens } from '@fluentui/react-components';
import { DocumentSearchRegular } from '@fluentui/react-icons';

import { useDocumentTitle, useSearchParams } from '@shared/hooks';
import { NoPage } from '@shared/pages';

import { FindingCard } from '../components/results/FindingCard';
import { FindingFilters } from '../components/results/FindingFilters';
import { ReviewProgress } from '../components/results/ReviewProgress';
import { ReviewSummaryCards } from '../components/results/ReviewSummaryCards';
import { EmptyState, useReviewStyles } from '../components/shared';
import { isReviewRunning } from '../helpers';
import { ReviewPageScaffold } from '../layouts/ReviewPageScaffold';
import { useReviewStore } from '../state/ReviewStore';
import { TReview } from '../types';
import { EMPTY_FINDING_FILTERS, filterFindings, hasActiveFilters, sortFindings, TFindingFilters } from '../utils';

const useStyles = makeStyles({
  findings: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

type TContentProps = {
  review: TReview;
  productId?: string;
};

const ReviewResults = ({ review, productId }: TContentProps) => {
  const classes = useStyles();
  const reviewClasses = useReviewStyles();
  const { reviewBases } = useReviewStore();
  const [filters, setFilters] = useState<TFindingFilters>(EMPTY_FINDING_FILTERS);

  const visibleFindings = useMemo(
    () => sortFindings(filterFindings(review.findings, filters)),
    [review.findings, filters]
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        <Subtitle1>{review.name}</Subtitle1>
        {review.productName ? <Body2 className={reviewClasses.muted}>{review.productName}</Body2> : null}
      </div>

      <ReviewSummaryCards review={review} bases={reviewBases} productId={productId} />

      <div className="flex flex-col gap-4 mt-4">
        <Subtitle1>
          {review.findings.length} finding{review.findings.length === 1 ? '' : 's'} identified
        </Subtitle1>
        <FindingFilters findings={review.findings} filters={filters} setFilters={setFilters} />

        {visibleFindings.length > 0 ? (
          <div className={classes.findings}>
            {visibleFindings.map((finding) => (
              <FindingCard key={finding.id} finding={finding} reviewId={review.id} productId={productId} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<DocumentSearchRegular />}
            title={hasActiveFilters(filters) ? 'No findings match the filters' : 'No findings'}
            message={
              hasActiveFilters(filters)
                ? 'Adjust or clear the filters to see more findings.'
                : 'This review did not raise any findings.'
            }
          />
        )}
      </div>
    </>
  );
};

const ReviewDetailContent = () => {
  const { reviewId } = useParams();
  const { searchParams } = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;
  const { getReview, reviewBases, now } = useReviewStore();

  const review = reviewId ? getReview(reviewId) : undefined;
  if (!review) return <NoPage />;

  return isReviewRunning(review, now) ? (
    <ReviewProgress review={review} bases={reviewBases} now={now} />
  ) : (
    <ReviewResults review={review} productId={productId} />
  );
};

const ReviewDetailPage = () => {
  const { reviewId } = useParams();
  const { getReview } = useReviewStore();
  const review = reviewId ? getReview(reviewId) : undefined;

  useDocumentTitle(review?.name ?? 'Review');

  return (
    <ReviewPageScaffold breadcrumbTail={review ? [{ name: review.name }] : []}>
      <ReviewDetailContent />
    </ReviewPageScaffold>
  );
};

export default ReviewDetailPage;
